import type {
    Campaign,
    CampaignCreativeAutoAssignRequest,
    CampaignCreativeMassUnAssignRequest,
    CampaignError,
    Displays_LineItem,
} from 'c-sdk';
import { CampaignErrorableType, Displays_LineItemLineItemType } from 'c-sdk';
import { omit } from 'ramda';

/**
 * Derivations behind the campaign displays (line items) tab and its row cells.
 */

type TablePagination =
    | {
          filters?: CampaignCreativeAutoAssignRequest['filters'] | null;
          searchables?: CampaignCreativeAutoAssignRequest['searchables'];
      }
    | null
    | undefined;

const creativeGroupFilter = (paginationData: TablePagination) => {
    const groups = paginationData?.filters?.creativeGroups;
    return Array.isArray(groups) && groups.length > 0 ? (groups as number[]) : null;
};

/**
 * Auto-assign follows the table's current filters: a creative group filter narrows the creatives,
 * every other filter (and the search) narrows the line items. Pass `lineItemIds` for one row.
 */
export const buildAutoAssignRequest = (
    paginationData: TablePagination,
    lineItemIds?: string[],
): CampaignCreativeAutoAssignRequest => {
    const request: CampaignCreativeAutoAssignRequest = lineItemIds
        ? { lineItems: lineItemIds }
        : {};
    const groups = creativeGroupFilter(paginationData);
    if (groups) request.creativeGroups = groups;
    request.filters = omit(['creativeGroups'], paginationData?.filters ?? {});
    request.searchables = paginationData?.searchables;
    return request;
};

/** Mass unassign follows the same filters, under the API's snake_case names. */
export const buildMassUnAssignRequest = (
    paginationData: TablePagination,
): CampaignCreativeMassUnAssignRequest => {
    const request: CampaignCreativeMassUnAssignRequest = {};
    const groups = creativeGroupFilter(paginationData);
    if (groups) request.creative_group_ids = groups;
    request.filters = omit(['creativeGroups'], paginationData?.filters ?? {});
    request.searchables = paginationData?.searchables;
    return request;
};

/** The tooltip on an assign button: its label, the API's error, or a generic failure. */
export const assignActionTooltip = (
    failed: boolean,
    error: unknown,
    t: (key: string) => string,
    labelKey: string,
    failedKey: string,
) => {
    if (!failed) return t(labelKey);
    if (String(error).length > 0) return String(error);
    return t(failedKey);
};

/** A row can be auto-assigned when nothing is running and creatives are available to it. */
export const canAutoAssignLineItem = (
    loading: boolean,
    availableCreativesCount: number | null | undefined,
) => !loading && (availableCreativesCount ?? 0) > 0;

/**
 * The creatives-count cells: a count of 0 is plain text, a positive count opens the dialog.
 * `none` renders nothing.
 */
export const creativesCountCell = (
    count: number | null | undefined,
): 'plain' | 'action' | 'none' => {
    if (count === 0) return 'plain';
    if (count != null && count > 0) return 'action';
    return 'none';
};

/** Resolutions of a line item, most common first, as `WxH`. */
export const lineItemResolutionLabels = (
    lineItem: Pick<Displays_LineItem, 'resolutions'> | null | undefined,
) =>
    [...(lineItem?.resolutions ?? [])]
        .sort((a, b) => (b?.total ?? 0) - (a?.total ?? 0))
        .map(res => `${res.width}x${res.height}`);

/**
 * How the resolutions cell reads: a site group with several resolutions shows the main one and a
 * "+N" chip; anything else lists them all.
 */
export const lineItemResolutionSummary = (
    lineItem: Pick<Displays_LineItem, 'resolutions' | 'type'> | null | undefined,
) => {
    const labels = lineItemResolutionLabels(lineItem);
    const extra = labels.length > 1 ? labels.slice(1) : [];

    if (lineItem?.type === Displays_LineItemLineItemType.SiteGroup && extra.length > 0) {
        return { main: labels[0], extra, text: null };
    }
    return { main: null, extra: [] as string[], text: labels.join(', ') };
};

/** The campaign errors raised against one line item. */
export const lineItemErrors = (
    errors: CampaignError[] | null | undefined,
    lineItemId: string | number | null | undefined,
) =>
    errors?.filter(
        err =>
            err.errorable_type === CampaignErrorableType.LineItem &&
            String(err.errorable_id) === String(lineItemId),
    ) ?? [];

/** A line item's booked start or end date, from the campaign's budget summary. */
export const budgetLineItemDate = (
    campaign: Pick<Campaign, 'budgetSummary'> | null | undefined,
    lineItemId: Displays_LineItem['id'] | null | undefined,
    column: 'start_date' | 'end_date',
) =>
    campaign?.budgetSummary?.lineItems?.find(
        ({ line_item_id }) => lineItemId != null && line_item_id === lineItemId,
    )?.[column] ?? null;
